import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface SueldoWidgetProps {
  user: any;
}

interface SueldoData {
  sueldo: {
    sueldoReferencia: number;
    sueldoFinal: number;
    porcentajeObjetivo: number;
    estado: string;
    mes: number;
    anio: number;
    auditoriasHechas: number;
    correccionesHechas: number;
    objetivoAuditorias?: number;
    objetivoCorrecciones?: number;
    montoDiasPagos: number;
    montoObjetivo: number;
    montoAuditorias: number;
    montoCorrecciones: number;
  };
  mensaje: string;
  detalle: {
    sueldoReferencia: number;
    diasPagos: {
      total: number;
      monto: number;
    };
    objetivo: {
      monto: number;
      porcentaje: number;
      montoGanado: number;
    };
    auditorias: {
      hechas: number;
      monto: number;
    };
    plus: {
      porcentaje: number;
      monto: number;
    };
    total: number;
  };
}

const SueldoWidget: React.FC<SueldoWidgetProps> = ({ user }) => {
  const [sueldoData, setSueldoData] = useState<SueldoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Helper para formatear números de manera segura
  const safeToFixed = (value: any, decimals: number = 1): string => {
    const num = parseFloat(value);
    return isNaN(num) ? '0' : num.toFixed(decimals);
  };

  useEffect(() => {
    // Mostrar sueldo para admin/contable SIEMPRE, y para otros solo si tienen permiso específico
    const puedeVerSueldo = 
      ['admin', 'contable'].includes(user?.role) || // Admin y contable siempre
      user?.permissions?.puedeVerSueldo === true; // O tiene permiso específico

    if (puedeVerSueldo) {
      loadSueldoData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadSueldoData = async () => {
    try {
      const response = await api.get('/sueldos/mi-sueldo');
      
      if (response.data.success) {
        setSueldoData(response.data.data);
      } else {
        setError(response.data.message || 'No se pudo cargar el sueldo');
      }
    } catch (err: any) {
      console.error('Error cargando sueldo:', err);
      setError('Error conectando con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount || 0);
  };

  const getProgressColor = (porcentaje: number) => {
    if (porcentaje < 30) return '#ef4444'; // Rojo
    if (porcentaje < 70) return '#f59e0b'; // Amarillo
    if (porcentaje < 100) return '#10b981'; // Verde
    return '#8b5cf6'; // Púrpura para más del 100%
  };

  const getMensajeMotivacional = (porcentaje: number) => {
    const hoy = new Date();
    const diasTranscurridos = hoy.getDate();
    const diasMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).getDate();
    const porcentajeMes = (diasTranscurridos / diasMes) * 100;
    
    // Categorías por día del mes
    const esPrincipioMes = diasTranscurridos <= 10;
    const esMedioMes = diasTranscurridos > 10 && diasTranscurridos <= 20;
    const esFinMes = diasTranscurridos > 20;
    
    // Lógica inteligente considerando día del mes + porcentaje
    if (porcentaje < 30) {
      if (esPrincipioMes) {
        return {
          icono: '📅',
          mensaje: 'Estás a principio de mes, ¡puedes recuperar y ganar más!'
        };
      } else if (esMedioMes) {
        return {
          icono: '⚡',
          mensaje: '¡Es momento de acelerar! Aún tienes tiempo para recuperarte.'
        };
      } else {
        return {
          icono: '🚨',
          mensaje: '¡Último esfuerzo! Quedan pocos días para alcanzar tu meta.'
        };
      }
    }
    
    if (porcentaje < 70) {
      if (esPrincipioMes) {
        return {
          icono: '👍',
          mensaje: '¡Buen inicio! Mantén este ritmo durante todo el mes.'
        };
      } else {
        return {
          icono: '💪',
          mensaje: `¡Vas bien para el día ${diasTranscurridos}! Mantén este ritmo.`
        };
      }
    }
    
    if (porcentaje < 100) {
      return {
        icono: '🔥',
        mensaje: `¡Excelente trabajo! Estás muy cerca del objetivo (día ${diasTranscurridos}).`
      };
    }
    
    if (porcentaje >= 100) {
      if (esPrincipioMes) {
        return {
          icono: '🚀',
          mensaje: '¡Increíble! Ya superaste tu objetivo y apenas comenzó el mes.'
        };
      } else {
        return {
          icono: '🎉',
          mensaje: `¡Felicidades! Superaste tu objetivo mensual (día ${diasTranscurridos}).`
        };
      }
    }
    
    return {
      icono: '📊',
      mensaje: 'Sigue trabajando para alcanzar tus metas.'
    };
  };

  // Solo mostrar widget si tiene permisos
  const puedeVerSueldo = 
    ['admin', 'contable'].includes(user?.role) || // Admin y contable siempre
    user?.permissions?.puedeVerSueldo === true; // O tiene permiso específico

  if (!puedeVerSueldo) {
    return null;
  }

  if (loading) {
    return (
      <div className="sueldo-widget loading">
        <div className="spinner"></div>
        <span>Cargando tu sueldo...</span>
      </div>
    );
  }

  if (error) {
    // Si es admin sin sueldo habilitado, no mostrar nada
    if (user?.role === 'admin' && error.includes('sin sueldo habilitado')) {
      return null;
    }
    
    return (
      <div className="sueldo-widget error">
        <h3>⚠️ Sueldo</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (!sueldoData || !sueldoData.sueldo) {
    return (
      <div className="sueldo-widget error">
        <h3>⚠️ Sueldo</h3>
        <p>No se encontraron datos de sueldo válidos</p>
      </div>
    );
  }

  try {
    const { sueldo, detalle } = sueldoData;
  
  // Log para debug
  // console.log('💰 SUELDO DATA:', {
  //   auditoriasHechas: sueldo?.auditoriasHechas,
  //   correccionesHechas: sueldo?.correccionesHechas,
  //   porcentajeObjetivo: sueldo?.porcentajeObjetivo,
  //   detalle_auditorias: detalle?.auditorias
  // });
    const progressColor = getProgressColor(sueldo?.porcentajeObjetivo || 0);
    const mensajeMotivacional = getMensajeMotivacional(sueldo?.porcentajeObjetivo || 0);

    return (
    <div className="sueldo-widget">
      <div className="sueldo-header">
        <h3>🎯 Tu Progreso del Mes</h3>
        <span className="mes-badge">
          {sueldo.mes}/{sueldo.anio}
        </span>
      </div>

      {/* Mensaje motivacional */}
      <div className="mensaje-motivacional" style={{ 
        backgroundColor: progressColor + '20',
        borderLeft: `4px solid ${progressColor}`,
        padding: '12px',
        marginBottom: '16px',
        borderRadius: '0 8px 8px 0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>
            {mensajeMotivacional.icono}
          </span>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
            {mensajeMotivacional.mensaje}
          </p>
        </div>
      </div>

      {/* Barra de progreso - Solo para admin */}
      {user.role === 'admin' && (
        <div className="progreso-container">
          <div className="progreso-header">
            <span className="progreso-texto">Progreso del objetivo</span>
            <span className="progreso-porcentaje" style={{ color: progressColor }}>
              {safeToFixed(sueldo.porcentajeObjetivo, 1)}%
            </span>
          </div>
          <div className="barra-progreso">
            <div 
              className="barra-relleno"
              style={{
                width: `${Math.min(sueldo.porcentajeObjetivo, 100)}%`,
                backgroundColor: progressColor,
                height: '8px',
                borderRadius: '4px',
                transition: 'all 0.3s ease'
              }}
            />
          </div>
        </div>
      )}

      {/* Progreso del mes - CANTIDADES SIN DINERO */}
      <div className="progreso-mes">
        <div className="progreso-detalle">
          <span>📋 Auditorías realizadas:</span>
          <span className="cantidad">{sueldo.auditoriasHechas || detalle?.auditorias?.hechas || 0}</span>
        </div>
        
        {user.role === 'supervisor' && (
          <div className="progreso-detalle">
            <span>✅ Correcciones realizadas:</span>
            <span className="cantidad" style={{ 
              color: (sueldo.correccionesHechas || 0) >= (sueldo.objetivoCorrecciones || 20) * 0.7 ? '#10b981' : '#ef4444'
            }}>
              {sueldo.correccionesHechas || 0}/{sueldo.objetivoCorrecciones || 20}
              {(sueldo.correccionesHechas || 0) < (sueldo.objetivoCorrecciones || 20) * 0.7 && ' 🚨'}
            </span>
          </div>
        )}

        <div className="progreso-detalle">
          <span>📅 Días trabajados:</span>
          <span className="cantidad">{new Date().getDate()}/30</span>
        </div>

        <div className="progreso-detalle">
          <span>⏰ Dinero ganado:</span>
          <span className="cantidad" style={{ color: '#10b981', fontWeight: 'bold' }}>
            {formatMoney(sueldo.montoAuditorias || 0)}
          </span>
        </div>

        <div className="progreso-detalle">
          <span>💼 Horas estimadas trabajadas:</span>
          <span className="cantidad">
            {Math.round((sueldo.auditoriasHechas || 0) * 2.5)} hrs
          </span>
        </div>

        <div className="progreso-detalle">
          <span>📊 Rendimiento del mes:</span>
          <span className="cantidad" style={{ 
            color: progressColor,
            fontWeight: 'bold'
          }}>
            {safeToFixed(sueldo.porcentajeObjetivo, 1)}% del objetivo
          </span>
        </div>

        {/* Progreso total - Solo para admin */}
        {user.role === 'admin' && (
          <div className="progreso-detalle total-progreso">
            <span><strong>🎯 Progreso total del mes:</strong></span>
            <span className="porcentaje-grande" style={{ color: progressColor }}>
              {safeToFixed(sueldo.porcentajeObjetivo, 1)}%
            </span>
          </div>
        )}
      </div>

      {/* Métricas del mes */}
      <div className="metricas-mes">
        <div className="metrica">
          <span className="metrica-numero">{sueldo.auditoriasHechas || detalle?.auditorias?.hechas || 0}</span>
          <span className="metrica-label">Auditorías</span>
        </div>
        
        {user.role === 'supervisor' && (
          <div className="metrica">
            <span className="metrica-numero">{sueldo.correccionesHechas || 0}</span>
            <span className="metrica-label">Correcciones</span>
          </div>
        )}

        {user.role === 'admin' && (
          <div className="metrica">
            <span 
              className="metrica-numero"
              style={{ color: progressColor }}
            >
              {safeToFixed(sueldo.porcentajeObjetivo, 0)}%
            </span>
            <span className="metrica-label">Objetivo</span>
          </div>
        )}
      </div>

      <style jsx>{`
        .sueldo-widget {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin-bottom: 20px;
          border: 1px solid #e5e7eb;
        }

        .sueldo-widget.loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 40px;
          color: #6b7280;
        }

        .sueldo-widget.error {
          border-left: 4px solid #ef4444;
          background-color: #fef2f2;
        }

        .sueldo-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .sueldo-header h3 {
          margin: 0;
          font-size: 18px;
          color: #1f2937;
        }

        .mes-badge {
          background: #3b82f6;
          color: white;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
        }

        .progreso-container {
          margin-bottom: 16px;
        }

        .progreso-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .progreso-texto {
          font-size: 14px;
          color: #6b7280;
        }

        .progreso-porcentaje {
          font-weight: 600;
          font-size: 16px;
        }

        .barra-progreso {
          width: 100%;
          height: 8px;
          background-color: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }

        .progreso-mes {
          margin-bottom: 16px;
        }

        .progreso-detalle {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          font-size: 14px;
          border-bottom: 1px solid #f3f4f6;
        }

        .progreso-detalle.total-progreso {
          border-top: 2px solid #e5e7eb;
          border-bottom: none;
          padding-top: 12px;
          margin-top: 8px;
        }

        .cantidad {
          font-weight: 600;
          color: #374151;
        }

        .porcentaje {
          font-weight: 600;
          font-size: 16px;
        }

        .porcentaje-grande {
          font-size: 18px;
          font-weight: 700;
        }

        .metricas-mes {
          display: flex;
          justify-content: space-around;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
        }

        .metrica {
          text-align: center;
        }

        .metrica-numero {
          display: block;
          font-size: 24px;
          font-weight: 700;
          color: #1f2937;
          line-height: 1;
        }

        .metrica-label {
          display: block;
          font-size: 12px;
          color: #6b7280;
          margin-top: 4px;
        }

        .spinner {
          border: 2px solid #f3f4f6;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
    );
  } catch (renderError) {
    console.error('Error renderizando SueldoWidget:', renderError);
    return (
      <div className="sueldo-widget error">
        <h3>⚠️ Error en Sueldo</h3>
        <p>Error temporal mostrando información</p>
      </div>
    );
  }
};

export default SueldoWidget;